import {HttpClient, HttpParams} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {Auth} from './auth';
import {Observable} from 'rxjs/Observable';
import 'rxjs/add/operator/map';
import 'rxjs/add/operator/catch';
import 'rxjs/add/observable/of';
import 'rxjs/add/observable/throw';
import {ProgramacionBundleResponse, PlansResponse, TrialStatusResponse, PaymentProvider, MaintainerCircuit, MaintainerUser, PaymentHistoryEntry, MaintainerPaginated} from './models';

// tv-claim-pairing: claim codes use the 54-symbol case-sensitive alphabet
// (mixed-case unambiguous, no 0/O/o or 1/I/i/L/l). Normalize by trimming
// surrounding whitespace ONLY — never case-fold, never trim interior chars.
export function normalizeClaimCode(code: string): string {
  return (code || '').trim();
}

// tv-claim-pairing slice-2b: the approve-claim quota rejection ships the
// backend envelope raw (res.status(err.code).send(err)), so the claim code
// arrives in `error.error` ('plan_limit_screens'), not `error.code` — while
// other 403s use `error.code`. Accept both shapes; never match on message
// text (server wording may change).
export function isPlanQuotaRejection(err: any): boolean {
  if (!err) {
    return false;
  }
  const body: any = err.error && typeof err.error === 'object' ? err.error : null;
  if (!body) {
    return false;
  }
  return body.code === 'plan_limit_screens' || body.error === 'plan_limit_screens';
}

// tv-claim-approve contract (frozen backend): {code, equipoId?} rebinding an
// existing equipo skips the quota gate (paywall still enforced server-side);
// {code, newEquipo:{name,grupoId}} creating a new equipo IS quota-gated.
export interface ApproveClaimBody {
  code: string;
  equipoId?: number;
  newEquipo?: { name: string, grupoId: number };
}

// Reachable claim states surfaced to the web account.
export type ClaimStatus = 'pending' | 'approved' | 'expired';

@Injectable()
export class Api {
  url; // api url
  constructor(public http: HttpClient,
              private auth: Auth) {
    this.url = this.auth.url;
  }

  /**** Web service Methods ****/

  getUrlUpload() {
    return this.url + '/api/admin/uploadFile';
  }

  getEstado() {
    const headers = this.auth.headersAuth();
    return this.http.get(this.url + '/api/admin/state', {headers: headers});
  }

  getProgramacion(): Observable<ProgramacionBundleResponse> {
    const headers = this.auth.headersAuth();
    return this.http.get<ProgramacionBundleResponse>(this.url + '/api/admin/programming', {headers: headers});
  }

  getProgramacionbyGrupo(id = null): Observable<ProgramacionBundleResponse> {
    const headers = this.auth.headersAuth();
    return this.http.get<ProgramacionBundleResponse>(this.url + '/api/admin/findByGrupo/' + id, {headers: headers});
  }

  getProgramacionbyPos(id = null): Observable<ProgramacionBundleResponse> {
    const headers = this.auth.headersAuth();
    return this.http.get<ProgramacionBundleResponse>(this.url + '/api/admin/findByPos/' + id, {headers: headers});
  }

  updateProgramacion(body) {
    const headers = this.auth.headersAuth();
    return this.http.post(this.url + '/api/admin/updateGrupo', body, {headers: headers});
  }

  updateProgramacionbyPos(body) {
    const headers = this.auth.headersAuth();
    return this.http.post(this.url + '/api/admin/updatePos', body, {headers: headers});
  }

  /*** Frames ***/

  addFrame(body) {
    const headers = this.auth.headersAuth();
    return this.http.post(this.url + '/api/frame', body, {headers: headers});
  }

  editFrame(body) {
    const headers = this.auth.headersAuth();
    return this.http.put(this.url + '/api/frame', body, {headers: headers});
  }

  deleteFrame(id) {
    const headers = this.auth.headersAuth();
    return this.http.delete(this.url + '/api/frame/' + id, {headers: headers});
  }

  uploadVideo(formData) {
    const headers = this.auth.headersFileAuth();
    return this.http.post(this.url + '/api/admin/uploadFile', formData, {headers: headers, reportProgress: true});
  }

  /*** Noticias ***/

  getNoticias(id) {
    const headers = this.auth.headersAuth();
    return this.http.get(this.url + '/api/noticias/' + id, {headers: headers});
  }

  editNoticia(body) {
    const headers = this.auth.headersAuth();
    return this.http.post(this.url + '/api/noticias', body, {headers: headers});
  }

  deleteNoticia(noticia) {
    const headers = this.auth.headersAuth();
    const path = this.url + '/api/noticias/' + noticia.id + '/' + noticia.idCircuito;
    return this.http.delete(path, {headers: headers});
  }

  createManualNewsFrame(formData: FormData) {
    const headers = this.auth.headersFileAuth();
    return this.http.post(this.url + '/api/noticias/manual-frame', formData, {headers: headers});
  }

  /*** Grupos ***/

  getSettings() {
    const headers = this.auth.headersAuth();
    return this.http.get(this.url + '/api/admin/getSettings', {headers: headers});
  }

  crearGrupo(body) {
    const headers = this.auth.headersAuth();
    return this.http.post(this.url + '/api/grupo', body, {headers: headers});
  }

  editarGrupo(grupo) {
    const headers = this.auth.headersAuth();
    return this.http.put(this.url + '/api/grupo', grupo, {headers: headers});
  }

  eliminarGrupo(grupo) {
    const headers = this.auth.headersAuth();
    const path = this.url + '/api/grupo/' + grupo.id;
    return this.http.delete(path, {headers: headers});
  }

  /** Equipos **/

  crearEquipo(body) {
    const headers = this.auth.headersAuth();
    return this.http.post(this.url + '/api/pos', body, {headers: headers});
  }

  editarEquipo(body) {
    const headers = this.auth.headersAuth();
    return this.http.put(this.url + '/api/pos', body, {headers: headers});
  }

  eliminarEquipo(equipo) {
    const headers = this.auth.headersAuth();
    const path = this.url + '/api/pos/' + equipo.id;
    return this.http.delete(path, {headers: headers});
  }

  /*** TV claim pairing (tv-claim-approve) ***/

  approveClaim(body: ApproveClaimBody) {
    const headers = this.auth.headersAuth();
    const payload: any = {code: normalizeClaimCode(body.code)};
    if (body.equipoId !== undefined && body.equipoId !== null) {
      payload.equipoId = body.equipoId;
    }
    if (body.newEquipo) {
      payload.newEquipo = body.newEquipo;
    }
    return this.http.post(this.url + '/api/pos/approve-claim', payload, {headers: headers});
  }

  // Unlink revokes the circuito→equipo binding via the existing DELETE
  // /api/pos/:id endpoint (no new backend contract). Tracker history from
  // GET /api/admin/state is kept server-side — only the binding is revoked,
  // so the TV requires re-pairing or manual IDs.
  unlinkEquipo(equipo) {
    const headers = this.auth.headersAuth();
    const path = this.url + '/api/pos/' + equipo.id;
    return this.http.delete(path, {headers: headers});
  }

  sendAction(id, action) {
    const headers = this.auth.headersAuth();
    const body = {id: id, action: action};
    return this.http.post(this.url + '/api/admin/addActionToPos', body, {headers: headers});
  }

  /*** Forecast ***/

  getForecast(coords) {
    const headers = this.auth.headersAuth();
    return this.http.get(this.url + '/api/forecast/' + encodeURIComponent(coords), {headers: headers});
  }

  /*** Invitations ***/

  inviteUser(email, roleId) {
    const headers = this.auth.headersAuth();
    return this.http.post(this.url + '/api/admin/inviteUser', {email: email, roleId: roleId}, {headers: headers});
  }

  createRecoveryLink(email) {
    const headers = this.auth.headersAuth();
    return this.http.post(this.url + '/api/admin/createRecoveryLink', {email: email}, {headers: headers});
  }

  setUserLockout(id, lockout) {
    const headers = this.auth.headersAuth();
    return this.http.post(this.url + '/api/admin/setUserLockout', {id: id, lockout: lockout}, {headers: headers});
  }

  setUserActivated(id, activated) {
    const headers = this.auth.headersAuth();
    return this.http.post(this.url + '/api/admin/setUserActivated', {id: id, activated: activated}, {headers: headers});
  }

  updateUser(data) {
    const headers = this.auth.headersAuth();
    return this.http.post(this.url + '/api/admin/updateUser', data, {headers: headers});
  }

  checkInvite(token) {
    return this.http.get(this.url + '/api/init/checkInvite/' + token);
  }

  getMyStaticCode() {
    const headers = this.auth.headersAuth();
    return this.http.get(this.url + '/api/admin/myStaticCode', {headers: headers});
  }

  changePassWithAdminCode(email, code, newPass) {
    const headers = this.auth.headers();
    return this.http.post(this.url + '/api/init/changePassWithAdminCode', {email: email, code: code, newPass: newPass}, {headers: headers});
  }

  verifyAdminCode(email, code) {
    const headers = this.auth.headers();
    return this.http.post(this.url + '/api/init/verifyAdminCode', {email: email, code: code}, {headers: headers});
  }

  // C3 additive passthrough (contratos-congelados T4): no mapping, no field
  // dropping — unknown future fields reach the entitlement store untouched.
  getTrialStatus(guid: string): Observable<TrialStatusResponse> {
    const headers = this.auth.headersAuth();
    return this.http.get<TrialStatusResponse>(this.url + '/api/circuito/' + guid + '/status', {headers: headers});
  }

  // invariant: GET /api/plans public before extraeToken (handler.js:25→27)
  getPlans(): Observable<PlansResponse> {
    const headers = this.auth.headers();
    return this.http.get<PlansResponse>(this.url + '/api/plans', {headers: headers});
  }

  getPaymentProviders(): Observable<{providers: PaymentProvider[]}> {
    var headers = this.auth.headers();
    var that: any = this;
    return this.http.get<{providers: PaymentProvider[]}>(this.url + '/api/payment-providers', {headers: headers})
      .catch(function(err: any): Observable<{providers: PaymentProvider[]}> {
        return that.getPlans().map(function(res: any): {providers: PaymentProvider[]} {
          var data = res && res.data ? res.data : res;
          var providers: any = data.providers;
          if (Array.isArray(providers) && providers.length) {
            return {providers: providers};
          }
          var paypal: any = data.paypal;
          if (paypal && paypal.clientId) {
            return {providers: [{id: 'paypal', name: 'PayPal', clientId: paypal.clientId, currency: paypal.currency || 'USD'}]};
          }
          return {providers: []};
        });
      }.bind(this)) as Observable<{providers: PaymentProvider[]}>;
  }

  paySubscription(guid: string, paymentId: string, paymentProviderOrPlanId?: string, planId?: string): Observable<any> {
    var provider = 'paypal';
    var finalPlanId: string = null;
    if (planId) {
      provider = paymentProviderOrPlanId || 'paypal';
      finalPlanId = planId;
    } else if (paymentProviderOrPlanId) {
      if (paymentProviderOrPlanId === 'paypal' || paymentProviderOrPlanId === 'manual' || paymentProviderOrPlanId === 'mercadopago' || paymentProviderOrPlanId === 'stripe') {
        provider = paymentProviderOrPlanId;
        finalPlanId = null;
      } else {
        provider = 'paypal';
        finalPlanId = paymentProviderOrPlanId;
      }
    }
    if (!finalPlanId) { console.warn('[deprecation] paySubscription without planId'); }
    var headers = this.auth.headersAuth();
    var body: any = {paymentId: paymentId, paymentProvider: provider};
    if (finalPlanId) { body.planId = finalPlanId; }
    return this.http.post(this.url + '/api/circuito/' + guid + '/pay', body, {headers: headers});
  }

  getMaintainerCircuits(params: {q?: string, status?: string, provider?: string, billing?: string, page?: number, limit?: number}): Observable<MaintainerPaginated<MaintainerCircuit>> {
    var headers = this.auth.headersAuth();
    var httpParams = new HttpParams();
    if (params) {
      if (params.q) { httpParams = httpParams.set('q', params.q); }
      if (params.status) { httpParams = httpParams.set('status', params.status); }
      if (params.provider) { httpParams = httpParams.set('provider', params.provider); }
      if (params.billing) { httpParams = httpParams.set('billing', params.billing); }
      if (params.page) { httpParams = httpParams.set('page', String(params.page)); }
      if (params.limit) { httpParams = httpParams.set('limit', String(params.limit)); }
    }
    return this.http.get(this.url + '/api/maintainer/circuits', {headers: headers, params: httpParams})
      .map(function(res: any): MaintainerPaginated<MaintainerCircuit> {
        var body = res && res.data ? res.data : res;
        // backend wraps as {circuits,total,page,limit,code,status}
        if (body && body.circuits) {
          return {data: body.circuits, circuits: body.circuits, total: body.total || 0, page: body.page || 1, limit: body.limit || 20} as MaintainerPaginated<MaintainerCircuit>;
        }
        if (body && body.data) {
          return {data: body.data, total: body.total || 0, page: body.page || 1, limit: body.limit || 20} as MaintainerPaginated<MaintainerCircuit>;
        }
        return body as MaintainerPaginated<MaintainerCircuit>;
      })
      .catch(function(err: any): Observable<MaintainerPaginated<MaintainerCircuit>> {
        return Observable.of({data: [], circuits: [], total: 0, page: 1, limit: 20} as MaintainerPaginated<MaintainerCircuit>);
      });
  }

  getMaintainerUsers(params: {q?: string, page?: number, limit?: number}): Observable<MaintainerPaginated<MaintainerUser>> {
    var headers = this.auth.headersAuth();
    var httpParams = new HttpParams();
    if (params) {
      if (params.q) { httpParams = httpParams.set('q', params.q); }
      if (params.page) { httpParams = httpParams.set('page', String(params.page)); }
      if (params.limit) { httpParams = httpParams.set('limit', String(params.limit)); }
    }
    return this.http.get(this.url + '/api/maintainer/users', {headers: headers, params: httpParams})
      .map(function(res: any): MaintainerPaginated<MaintainerUser> {
        var body = res && res.data ? res.data : res;
        if (body && body.users) {
          return {data: body.users, users: body.users, total: body.total || 0, page: body.page || 1, limit: body.limit || 20} as MaintainerPaginated<MaintainerUser>;
        }
        if (body && body.data) {
          return {data: body.data, total: body.total || 0, page: body.page || 1, limit: body.limit || 20} as MaintainerPaginated<MaintainerUser>;
        }
        return body as MaintainerPaginated<MaintainerUser>;
      })
      .catch(function(err: any): Observable<MaintainerPaginated<MaintainerUser>> {
        return Observable.of({data: [], users: [], total: 0, page: 1, limit: 20} as MaintainerPaginated<MaintainerUser>);
      });
  }

  getMaintainerPayments(guid: string, page: number, limit: number): Observable<MaintainerPaginated<PaymentHistoryEntry>> {
    var headers = this.auth.headersAuth();
    var httpParams = new HttpParams();
    if (page) { httpParams = httpParams.set('page', String(page)); }
    if (limit) { httpParams = httpParams.set('limit', String(limit)); }
    return this.http.get(this.url + '/api/maintainer/circuito/' + guid + '/payments', {headers: headers, params: httpParams})
      .map(function(res: any): MaintainerPaginated<PaymentHistoryEntry> {
        var body = res && res.data ? res.data : res;
        if (body && body.payments) {
          // normalize snake/camel both ways — keep both for compat, history modal fix
          var list: any[] = body.payments;
          var normalized = list.map(function(p: any): any {
            var ref = p.payment_reference !== undefined ? p.payment_reference : p.paymentReference;
            var provider = p.payment_provider !== undefined ? p.payment_provider : p.paymentProvider;
            var plan = p.plan_id !== undefined ? p.plan_id : p.planId;
            var billing = p.billing_cycle !== undefined ? p.billing_cycle : p.billingCycle;
            var created = p.created_at !== undefined ? p.created_at : p.createdAt;
            var cid = p.circuito_id !== undefined ? p.circuito_id : p.circuitoId;
            return {
              id: p.id,
              circuito_id: cid,
              circuitoId: cid,
              payment_provider: provider,
              paymentProvider: provider,
              payment_reference: ref,
              paymentReference: ref,
              plan_id: plan,
              planId: plan,
              amount: p.amount,
              currency: p.currency,
              billing_cycle: billing,
              billingCycle: billing,
              created_at: created,
              createdAt: created
            };
          });
          return {data: normalized, payments: normalized, total: body.total || 0, page: body.page || 1, limit: body.limit || 20} as MaintainerPaginated<PaymentHistoryEntry>;
        }
        return body as MaintainerPaginated<PaymentHistoryEntry>;
      })
      .catch(function(err: any): Observable<MaintainerPaginated<PaymentHistoryEntry>> {
        return Observable.of({data: [], payments: [], total: 0, page: 1, limit: 20} as MaintainerPaginated<PaymentHistoryEntry>);
      });
  }

  maintainerEnable(guid: string, body: {billingCycle: string, planId?: string, paymentProvider?: string, notes?: string, amount?: string}): Observable<any> {
    var headers = this.auth.headersAuth();
    var payload: any = {billingCycle: body.billingCycle};
    if (body.planId) { payload.planId = body.planId; }
    if (body.paymentProvider) { payload.paymentProvider = body.paymentProvider; }
    else { payload.paymentProvider = 'manual'; }
    if (body.notes !== undefined && body.notes !== null) { payload.notes = body.notes; }
    if (body.amount !== undefined && body.amount !== null && String(body.amount).trim() !== '') { payload.amount = String(body.amount).trim(); }
    return this.http.post(this.url + '/api/maintainer/circuito/' + guid + '/enable', payload, {headers: headers})
      .map(function(res: any) { return res; })
      .catch(function(err: any) { return Observable.throw(err); });
  }

  maintainerDisable(guid: string): Observable<any> {
    var headers = this.auth.headersAuth();
    return this.http.post(this.url + '/api/maintainer/circuito/' + guid + '/disable', {}, {headers: headers})
      .map(function(res: any) { return res; })
      .catch(function(err: any) { return Observable.throw(err); });
  }

  maintainerExtend(guid: string, body: {months: number}): Observable<any> {
    var headers = this.auth.headersAuth();
    return this.http.post(this.url + '/api/maintainer/circuito/' + guid + '/extend', body, {headers: headers})
      .map(function(res: any) { return res; })
      .catch(function(err: any) { return Observable.throw(err); });
  }

  maintainerChangePlan(guid: string, body: {planId: string}): Observable<any> {
    var headers = this.auth.headersAuth();
    return this.http.post(this.url + '/api/maintainer/circuito/' + guid + '/change-plan', body, {headers: headers})
      .map(function(res: any) { return res; })
      .catch(function(err: any) { return Observable.throw(err); });
  }

  maintainerLockout(userId: number, body: {lockout: boolean}): Observable<any> {
    var headers = this.auth.headersAuth();
    return this.http.post(this.url + '/api/maintainer/user/' + userId + '/lockout', body, {headers: headers})
      .map(function(res: any) { return res; })
      .catch(function(err: any) { return Observable.throw(err); });
  }

  revealPayment(guid: string): Observable<{paymentReference: string}> {
    var headers = this.auth.headersAuth();
    return this.http.post(this.url + '/api/maintainer/circuito/' + guid + '/reveal-payment', {}, {headers: headers})
      .map(function(res: any): {paymentReference: string} {
        var body = res && res.data ? res.data : res;
        var ref = body.paymentReference || body.payment_reference || body.paymentReferenceMasked || null;
        if (ref === null && body && typeof body === 'object' && body.payment_reference !== undefined) {
          ref = body.payment_reference;
        }
        return {paymentReference: ref};
      })
      .catch(function(err: any) { return Observable.throw(err); });
  }

}



// WEBPACK FOOTER //
// ./src/app/providers/api.ts